import type { ReactNode } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { IconTooltip } from '@/components/common/IconTooltip';
import type { WorkbookSheetKind } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WorkbookItemIcon } from './workbookItemIcon';
import { SHEET_KIND_ICONS } from './workbookItemIconKind';
import { SHEET_KINDS, SHEET_KIND_UI } from './sheetKindUi';

function KindItem({
  icon,
  title,
  description,
  disabled,
  onSelect,
}: {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  disabled?: boolean;
  onSelect?: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onClick={onSelect}
      className="items-start"
    >
      {icon}
      <span className="flex max-w-64 flex-col gap-0.5">
        <span>{title}</span>
        <span className="text-xs text-muted-foreground">{description}</span>
      </span>
    </DropdownMenuItem>
  );
}

/**
 * Add Worksheet, as a choice of sheet kinds side by side: a columnar sheet,
 * a pivot table, or a freeform sheet.
 */
export function WorkbookAddSheetMenu({
  label,
  disabled,
  onAdd,
}: {
  label: string;
  disabled: boolean;
  onAdd: (kind: WorkbookSheetKind) => void;
}) {
  return (
    <DropdownMenu>
      <IconTooltip label={label}>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="size-6"
            disabled={disabled}
            aria-label={label}
          >
            <PlusIcon className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
      </IconTooltip>
      <DropdownMenuContent align="start">
        {SHEET_KINDS.map((kind) => {
          const { title: Title, description: Description } =
            SHEET_KIND_UI[kind];
          return (
            <KindItem
              key={kind}
              icon={<WorkbookItemIcon kind={SHEET_KIND_ICONS[kind]} />}
              title={<Title />}
              description={<Description />}
              onSelect={() => onAdd(kind)}
            />
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
